// Portamotores: tubos (de cuerpo o interiores) con la marca `motorMount`. Ver docs/theory/motors.md.
import { attachedFront, layoutStack, type Layout } from './geometry';
import type { BodyTube, InnerTube, MotorMountMark, MotorSelection, Rocket, StackComponent, Transition } from './types';

export type MountTube = BodyTube | InnerTube;

/** Portamotor del diseño: el tubo marcado, su marca y el cuerpo de la pila que lo contiene. */
export interface Mount {
  readonly tube: MountTube;
  readonly mark: MotorMountMark;
  readonly parent: Layout;
  /** Longitud del tubo (m). */
  readonly length: number;
  /** Diámetro interior del tubo (m): el máximo que admite un motor. */
  readonly innerDiameter: number;
  /** Posición axial del extremo trasero del tubo, en m desde la punta. */
  readonly aftX: number;
}

export const mountInnerDiameter = (t: MountTube): number => (t.type === 'inner-tube' ? t.innerDiameter : t.outerDiameter - 2 * t.wallThickness);

/** Portamotores en orden de pila: un tubo de cuerpo marcado va antes que sus tubos interiores. */
export function listMounts(rocket: Rocket): Mount[] {
  const out: Mount[] = [];
  for (const entry of layoutStack(rocket)) {
    const c = entry.component;
    if (c.type === 'nose-cone') continue;
    if (c.type === 'body-tube' && c.motorMount !== undefined) {
      out.push({ tube: c, mark: c.motorMount, parent: entry, length: c.length, innerDiameter: mountInnerDiameter(c), aftX: entry.x1 });
    }
    for (const k of c.children) {
      if (k.type !== 'inner-tube' || k.motorMount === undefined) continue;
      out.push({ tube: k, mark: k.motorMount, parent: entry, length: k.length, innerDiameter: k.innerDiameter, aftX: attachedFront(entry, k.length, k.position) + k.length });
    }
  }
  return out;
}

/** Extremo trasero del motor de un portamotor: el del tubo más el voladizo. */
export const motorAftX = (m: Mount): number => m.aftX + (m.mark.overhang ?? 0);

/** Tubos que pueden marcarse como portamotor (de cuerpo e interiores), en orden de pila. */
export function mountCandidates(rocket: Rocket): MountTube[] {
  const out: MountTube[] = [];
  for (const c of rocket.stack) {
    if (c.type === 'nose-cone') continue;
    if (c.type === 'body-tube') out.push(c);
    for (const k of c.children) if (k.type === 'inner-tube') out.push(k);
  }
  return out;
}

/**
 * Copia del diseño con el tubo `tubeId` transformado por `fn`. Devuelve el mismo objeto si `fn` no cambia nada, para que
 * comparar por referencia siga diciendo si el diseño cambió.
 */
export function mapMountTube(rocket: Rocket, tubeId: string, fn: (t: MountTube) => MountTube): Rocket {
  let changed = false;
  const stack = rocket.stack.map((c): StackComponent => {
    if (c.type === 'nose-cone') return c;
    let next: BodyTube | Transition = c;
    if (c.type === 'body-tube' && c.id === tubeId) {
      const t = fn(c) as BodyTube;
      if (t !== c) next = t;
    }
    let kidsChanged = false;
    const children = next.children.map((k) => {
      if (k.type !== 'inner-tube' || k.id !== tubeId) return k;
      const t = fn(k) as InnerTube;
      if (t !== k) kidsChanged = true;
      return t;
    });
    if (kidsChanged) next = { ...next, children };
    if (next !== c) changed = true;
    return next;
  });
  return changed ? { ...rocket, stack } : rocket;
}

/** Pone o quita la marca de portamotor de un tubo; al quitarla se va también su motor. */
export function setMountMark(rocket: Rocket, tubeId: string, mark: MotorMountMark | undefined): Rocket {
  return mapMountTube(rocket, tubeId, (t) => {
    if (mark === undefined) {
      if (t.motorMount === undefined) return t;
      const { motorMount: _drop, ...rest } = t;
      void _drop;
      return rest as MountTube;
    }
    return t.motorMount === mark ? t : ({ ...t, motorMount: mark } as MountTube);
  });
}

/** Pone o quita el motor de un tubo marcado; un tubo sin marca no cambia. */
export function setMountMotor(rocket: Rocket, tubeId: string, motor: MotorSelection | undefined): Rocket {
  return mapMountTube(rocket, tubeId, (t) => {
    const mark = t.motorMount;
    if (mark === undefined) return t;
    const same = motor === undefined ? mark.motor === undefined : mark.motor !== undefined && mark.motor.motorId === motor.motorId && mark.motor.ejectionDelay === motor.ejectionDelay;
    if (same) return t;
    const { motor: _old, ...rest } = mark;
    void _old;
    return { ...t, motorMount: motor === undefined ? rest : { ...rest, motor } } as MountTube;
  });
}
